import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar, LoadingSpinner } from '../components';
import { partyEngineService } from '../services';
import { useUser, useAuth } from '../context';
import { useDialog } from '../context/DialogContext';

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const when = (iso) => new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).toUpperCase();

/**
 * /host/claim — parties Portal seeded that are still looking for a host.
 * The private brief (venue candidate, cost estimate, what the host clears at goal)
 * is only served to eligible hosts; everyone else sees why they can't claim yet.
 */
export default function ClaimPartiesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();
  const { showConfirm, showAlert } = useDialog();

  const runId = searchParams.get('run');
  const regionId = searchParams.get('region');
  const [regions, setRegions] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const [r, p] = await Promise.all([
      partyEngineService.getClaimableRegions(),
      partyEngineService.getClaimable({ regionId: regionId || undefined, runId: runId || undefined }),
    ]);
    if (r.success) setRegions(r.data || []);
    if (p.success) setParties(p.data || []);
    setLoading(false);
  }, [regionId, runId]);

  useEffect(() => { load(); }, [load]);

  const eligible = useMemo(() => parties.some((p) => p.hostBrief != null || p.venueCostBasis !== 'none' || p.hostMarginCentsAtGoal > 0), [parties]);

  const claim = async (party) => {
    const ok = await showConfirm({
      title: 'CLAIM THIS EVENT?',
      message: `"${party.title}" becomes yours to host. Tickets already sold (${money(party.pledgedCents)}) move to your wallet's escrow, and you run it like any event you created.`,
      confirmLabel: 'CLAIM IT',
    });
    if (!ok) return;
    setClaiming(party.eventId);
    const r = await partyEngineService.claim(party.eventId);
    setClaiming(null);
    if (!r.success) { await showAlert({ title: 'NOT THIS TIME', message: r.error || 'Could not claim.', type: 'error' }); await load(); return; }
    navigate(`/host/events/${party.eventId}`);
  };

  const pickRegion = (id) => {
    const next = new URLSearchParams(searchParams);
    next.delete('run');
    if (id) next.set('region', id); else next.delete('region');
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-inside-bg">
      <Navbar user={profile} walletBalance={walletBalance} onLogout={async () => { await logout(); navigate('/'); }} onLogoClick={() => navigate('/host')} />
      <main className="max-w-6xl mx-auto px-4 py-8 font-mono">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-wider text-black mb-1">EVENTS LOOKING FOR A HOST.</h1>
          <p className="text-sm text-gray-600">Portal seeded these as Party Starters. Claim one and it's yours to run — same terms as any party you'd start yourself.</p>
        </div>

        {regions.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-6">
            <button type="button" onClick={() => pickRegion(null)} className={`px-3 py-2 border-2 border-black text-xs font-bold uppercase tracking-wider transition-colors ${!regionId ? 'bg-black text-white' : 'bg-white hover:bg-black hover:text-white'}`}>ALL</button>
            {regions.map((r) => (
              <button key={r.id} type="button" onClick={() => pickRegion(r.id)} className={`px-3 py-2 border-2 border-black text-xs font-bold uppercase tracking-wider transition-colors ${regionId === r.id ? 'bg-black text-white' : 'bg-white hover:bg-black hover:text-white'}`}>{r.name}</button>
            ))}
          </div>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : parties.length === 0 ? (
          <div className="border-2 border-black bg-white p-14 text-center">
            <p className="text-sm font-bold uppercase tracking-widest">NOTHING LOOKING FOR A HOST RIGHT NOW.</p>
            <p className="text-xs text-gray-500 mt-2 uppercase tracking-wider">WHEN THE ENGINE SEEDS AN EVENT NEAR YOU, IT LANDS HERE.</p>
          </div>
        ) : (
          <>
            {!eligible && (
              <div className="border-2 border-black bg-white p-4 mb-6 text-xs uppercase tracking-wider text-gray-700">
                CLAIMING IS OPEN TO HOSTS WHO HAVE PUBLISHED AN EVENT ON PORTAL WITH A VERIFIED EMAIL. RUN ONE EVENT FIRST AND THESE UNLOCK.
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
              {parties.map((p) => {
                const pct = p.fundingGoalCents > 0 ? Math.min(100, Math.round(p.pledgedCents * 100 / p.fundingGoalCents)) : 0;
                const isOpen = !!open[p.eventId];
                return (
                  <div key={p.eventId} className="border-2 border-black bg-white flex">
                    <div className="border-r-2 border-black bg-black flex-none" style={{ width: '30%', maxWidth: 180, minWidth: 96, minHeight: 240 }}>
                      {p.posterUrl
                        ? <img src={p.posterUrl} alt="" className="w-full h-full object-cover block" />
                        : <div className="h-full flex items-center justify-center text-white text-[10px] font-bold uppercase tracking-widest">PARTY STARTER</div>}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="p-4 flex flex-col gap-2">
                        <div className="flex items-start justify-between gap-3">
                          <h2 className="text-lg font-semibold leading-tight">{p.title}</h2>
                          <span className="text-[10px] font-bold uppercase tracking-wider border-2 border-black px-2 py-1 whitespace-nowrap">HOST WANTED</span>
                        </div>
                        <div className="text-xs uppercase tracking-wider text-gray-600">{when(p.dateTime)} · {p.regionName}</div>
                        <div className="text-xs uppercase tracking-wider">
                          {money(p.ticketPriceCents)} A TICKET · {p.minGuests} TO GO · DEADLINE {when(p.fundingDeadline)}
                        </div>
                        <div className="border-2 border-black h-3 bg-white"><div className="h-full bg-black" style={{ width: `${pct}%` }} /></div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">{money(p.pledgedCents)} OF {money(p.fundingGoalCents)} PLEDGED · {p.paidCount} IN</div>
                      </div>

                      {p.hostBrief != null && (
                        <>
                          <div className="border-t-2 border-black" style={{ background: '#000', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            {[
                              ['VENUE (VERIFIED FOR HIRE)', p.venueName || (p.venueKind ? `a ${p.venueKind}` : 'Your call'), true],
                              ['VENUE RENTAL', `${p.venueCostBasis === 'llm_estimate' ? '≈ ' : ''}${money(p.venueCostEstimateCents || 0)}${p.venueCostBasis === 'pro_listing' ? ' (PRO LISTING)' : p.venueCostBasis === 'web_verified' ? ' (POSTED)' : ' (ESTIMATE)'}`],
                              ['PER HEAD', `≈ ${money(p.perHeadCostCents)}`],
                              ['7% FEE AT GOAL', money(p.hostFeeCents)],
                              ['YOU CLEAR AT GOAL', money(p.hostMarginCentsAtGoal)],
                            ].map(([k, v, full]) => (
                              <div key={k} className="bg-white flex items-center justify-between gap-2 px-3 py-2" style={{ gridColumn: full ? '1 / -1' : undefined }}>
                                <span className="text-[11px] uppercase tracking-wider text-gray-500 whitespace-nowrap">{k}</span>
                                <span className="text-[11px] font-bold truncate">{v}</span>
                              </div>
                            ))}
                          </div>
                          <button type="button" onClick={() => setOpen((o) => ({ ...o, [p.eventId]: !isOpen }))} className="border-t-2 border-black w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-gray-100">
                            <span>HOST BRIEF</span><span>{isOpen ? '−' : '+'}</span>
                          </button>
                          {isOpen && (
                            <div className="border-t-2 border-black px-3 py-3 text-xs leading-relaxed">
                              {p.venueEvidenceUrl && (
                                <p className="mb-2 text-[11px] uppercase tracking-wider"><a href={p.venueEvidenceUrl} target="_blank" rel="noreferrer" className="underline">VENUE'S PRIVATE-HIRE PAGE ↗</a></p>
                              )}
                              <p>{p.hostBrief}</p>
                              {p.rationale && <p className="mt-3 text-gray-600">{p.rationale}</p>}
                            </div>
                          )}
                        </>
                      )}

                      <div className="mt-auto border-t-2 border-black p-3 flex flex-wrap gap-2">
                        <button type="button" onClick={() => navigate(`/e/${p.inviteCode}`)} className="px-4 py-2 border-2 border-black bg-white text-[11px] font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors">VIEW EVENT →</button>
                        {p.hostBrief != null && (
                          <button type="button" disabled={claiming === p.eventId} onClick={() => claim(p)} className="px-4 py-2 border-2 border-black bg-black text-white text-[11px] font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-colors disabled:opacity-50">
                            {claiming === p.eventId ? 'CLAIMING…' : 'CLAIM THIS EVENT'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
